import { Boxes } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { ScriptElement, ThreeDContent } from '@/lib/schema.types';

let modelViewerLoadPromise: Promise<void> | null = null;

function loadModelViewer(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (modelViewerLoadPromise) return modelViewerLoadPromise;
  modelViewerLoadPromise = import('@google/model-viewer')
    .then(() => undefined)
    .catch((err) => {
      modelViewerLoadPromise = null;
      throw err;
    });
  return modelViewerLoadPromise;
}

function asThreeD(content: unknown): ThreeDContent {
  if (typeof content === 'string') return { src: content };
  if (content && typeof content === 'object') return content as ThreeDContent;
  return { src: '' };
}

interface Props {
  element: ScriptElement;
  /**
   * When `true`, the viewer fills its parent (used by freeform mode where
   * the wrapper sets explicit width/height). When `false`, it uses the
   * element's `style.width` / `style.height` if set, otherwise falls back to
   * an `aspect-video` box that fills the column width.
   */
  fill?: boolean;
}

export function ThreeDElement({ element, fill }: Props) {
  const data = asThreeD(element.content);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const w = element.style?.width as number | string | undefined;
  const h = element.style?.height as number | string | undefined;

  const sizingStyle: React.CSSProperties = fill
    ? { width: '100%', height: '100%' }
    : {
        width: w !== undefined ? (typeof w === 'number' ? `${w}px` : w) : '100%',
        height: h !== undefined ? (typeof h === 'number' ? `${h}px` : h) : undefined,
        maxWidth: '100%',
      };
  const sizingClass = fill
    ? 'overflow-hidden rounded-md'
    : h === undefined
      ? 'aspect-video overflow-hidden rounded-md'
      : 'overflow-hidden rounded-md';

  useEffect(() => {
    if (!data.src) return;
    let cancelled = false;
    loadModelViewer()
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load 3D viewer.');
      });
    return () => {
      cancelled = true;
    };
  }, [data.src]);

  if (!data.src) {
    return (
      <div
        style={sizingStyle}
        className={`${sizingClass} flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700`}
      >
        <Boxes size={28} />
        <span className="text-xs font-medium">3D model — paste a .glb / .gltf URL</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={sizingStyle}
        className={`${sizingClass} flex flex-col items-center justify-center gap-2 bg-red-50 p-3 text-center text-xs text-red-600`}
      >
        <Boxes size={20} />
        {error}
      </div>
    );
  }

  if (!ready) {
    return (
      <div
        style={sizingStyle}
        className={`${sizingClass} flex flex-col items-center justify-center gap-2 bg-bg-subtle text-fg-subtle`}
      >
        <Boxes size={20} className="animate-pulse" />
        <span className="text-[11px] font-medium">Loading 3D viewer…</span>
      </div>
    );
  }

  return (
    <div
      style={sizingStyle}
      className={`${sizingClass} bg-gradient-to-br from-slate-100 to-slate-200`}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* @ts-expect-error -- model-viewer is a custom element registered at runtime */}
      <model-viewer
        src={data.src}
        poster={data.poster || undefined}
        alt={data.alt || '3D model'}
        camera-controls={data.cameraControls !== false ? '' : undefined}
        auto-rotate={data.autoRotate !== false ? '' : undefined}
        exposure={data.exposure ?? 1}
        shadow-intensity="1"
        interaction-prompt="none"
        style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
      />
    </div>
  );
}
